import { useContext, useEffect } from "react";

import {
  ExportButton,
  PageSizeSelect,
  PaginationButton,
  PaginationContainer,
  PaginationControls,
  PaginationInfo,
} from "./AdminPage.styles";
import { exportTableCsv, ITableCsvExport } from "./tableCsv";
import { TableExportContext } from "./TableExportContext";

/**
 * The page/size state a table footer needs — satisfied by `useMockPagination`
 * for client-side lists and by a server-paginated query's own response.
 */
export interface ITablePaginationState {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  /** The server stopped counting at `totalItems`: show it as "N+". */
  totalIsLowerBound?: boolean;
}

interface ITablePaginationProps<T> {
  pagination: ITablePaginationState;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
  /**
   * Turns on the CSV button beside the row count. Pass the table's own
   * column defs and the rows to write — for a client-paginated list that is
   * `allItems` from `useMockPagination` (everything the search matches, not
   * the page on screen); for a server-paginated one it can only be the page
   * that has been fetched, which the button's count makes plain.
   */
  exportCsv?: ITableCsvExport<T>;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50];

/**
 * Footer row for the migrated /admin tables: "Showing x - y of z" plus the
 * page-size select and Previous/Next buttons. The same block was copied into
 * every migrated page; it lives here so the pages that follow inherit it
 * (the earlier pages still carry their inline copy — adopt this when one of
 * them is next touched).
 *
 * `totalItems` drives the range, so it works unchanged for a server-paginated
 * list where the current page holds fewer rows than the total.
 */
export function TablePagination<T>({
  pagination,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  exportCsv,
}: ITablePaginationProps<T>) {
  const { currentPage, pageSize, totalPages, totalItems } = pagination;
  const firstRow = (currentPage - 1) * pageSize + 1;
  const lastRow = Math.min(currentPage * pageSize, totalItems);

  // Inside a `TableContainer` the export moves up to the table header, beside
  // Refresh (see `TableWrapper`); this footer just publishes the config. With
  // no container around it, the footer keeps its own button as before.
  const exportContext = useContext(TableExportContext);
  const publish = exportContext?.publish;
  useEffect(() => {
    if (!publish) return undefined;
    publish((exportCsv as ITableCsvExport<unknown> | undefined) ?? null);
    return () => publish(null);
  }, [publish, exportCsv]);
  const showFooterExport = Boolean(exportCsv) && !exportContext;

  return (
    <PaginationContainer>
      <PaginationInfo>
        {totalItems > 0
          ? `Showing ${firstRow} - ${lastRow} of ${totalItems}${
              pagination.totalIsLowerBound ? "+" : ""
            }`
          : "No records"}
        {showFooterExport && exportCsv && (
          <ExportButton
            type="button"
            disabled={exportCsv.rows.length === 0}
            title={`Download ${exportCsv.rows.length} row${
              exportCsv.rows.length === 1 ? "" : "s"
            } as a CSV file`}
            onClick={() => exportTableCsv(exportCsv)}
          >
            Export CSV
          </ExportButton>
        )}
      </PaginationInfo>
      <PaginationControls>
        <PageSizeSelect
          aria-label="Rows per page"
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size} per page
            </option>
          ))}
        </PageSizeSelect>
        <PaginationButton
          onClick={() => onPageChange(currentPage - 1)}
          disabled={!pagination.hasPreviousPage}
        >
          Previous
        </PaginationButton>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <PaginationButton
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!pagination.hasNextPage}
        >
          Next
        </PaginationButton>
      </PaginationControls>
    </PaginationContainer>
  );
}
